"use client";

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useRouter } from "@/lib/navigation";
import {
  LayoutDashboard,
  ClipboardList,
  Ticket,
  CheckSquare,
  Megaphone,
  FileStack,
  Users,
  CreditCard,
  Settings,
  Building2,
  Store,
  DashboardLayout,
  useEntitlements,
  DropdownMenuItem,
  DropdownMenuSeparator,
  type NavItem,
} from "@repo/ui";
import { useApiClient, useSession } from "@repo/api-client";
import { useMessages } from "@/locales";
import { PortalNotificationBell } from "@/components/notification-bell";
import { LanguageSwitcher } from "@/components/language-switcher";

export default function DashboardShellLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const m = useMessages();
  const router = useRouter();
  const pathname = usePathname();
  const { user, accessToken, logout } = useSession();
  const api = useApiClient();
  const { included } = useEntitlements();
  const pendingDecisions = useQuery({
    queryKey: ["client-consents", "nav-count"],
    queryFn: () =>
      api.logistics.clientConsents.list({ status: "PENDING", limit: 50 }),
    enabled:
      Boolean(accessToken) && included("feature.module.logistics-workflow"),
    staleTime: 30_000,
  });
  const waitingDecisions = pendingDecisions.data?.items.length ?? 0;

  React.useEffect(() => {
    if (!accessToken) router.replace("/login");
  }, [accessToken, router]);

  React.useEffect(() => {
    if (!included("feature.module.billing_accounts") && pathname.startsWith("/billing")) router.replace("/");
  }, [included, pathname, router]);

  // Wait for session bootstrap so PermissionGate is not fed empty permissions
  // (blank main pane) while missing entitlement keys still look "included".
  if (
    !accessToken ||
    !user ||
    (!included("feature.module.billing_accounts") &&
      pathname.startsWith("/billing"))
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        …
      </div>
    );
  }

  const isClientAdmin = user?.clientOrgRole === "CLIENT_ADMIN";
  const isAgentOrg = user?.clientOrgEngagement === "AGENT";

  const operationsItems: NavItem[] = [
    {
      id: "home",
      label: m.nav.home,
      href: "/",
      active: pathname === "/",
      icon: <LayoutDashboard strokeWidth={1.6} />,
    },
    ...(included("feature.module.logistics-workflow") ? [{
      id: "order-services",
      label: m.nav.orderServices,
      href: "/order-services",
      active: pathname.startsWith("/order-services"),
      icon: <ClipboardList strokeWidth={1.6} />,
    } satisfies NavItem,
    {
      id: "master-documents",
      label: m.nav.masterDocuments,
      href: "/master-documents",
      active: pathname.startsWith("/master-documents"),
      icon: <FileStack strokeWidth={1.6} />,
    } satisfies NavItem] : []),
  ];

  const inboxItems: NavItem[] = [
    ...(included("feature.module.logistics-workflow") ? [{
      id: "approvals",
      label: m.nav.approvals,
      href: "/approvals",
      active: pathname.startsWith("/approvals"),
      icon: <CheckSquare strokeWidth={1.6} />,
      ...(waitingDecisions > 0
        ? { count: waitingDecisions, alert: true }
        : {}),
    } satisfies NavItem] : []),
    ...(included("feature.module.ticketing") ? [{
      id: "tickets",
      label: m.nav.tickets,
      href: "/tickets",
      active: pathname.startsWith("/tickets"),
      icon: <Ticket strokeWidth={1.6} />,
    } satisfies NavItem] : []),
    ...(included("feature.module.announcement")
      ? [
          {
            id: "announcements",
            label: m.nav.announcements,
            href: "/announcements",
            active: pathname.startsWith("/announcements"),
            icon: <Megaphone strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
  ];

  const orgItems: NavItem[] = [
    ...(isClientAdmin
      ? [
          ...(isAgentOrg
            ? [
                {
                  id: "end-customers",
                  label: m.nav.endCustomers,
                  href: "/end-customers",
                  active: pathname.startsWith("/end-customers"),
                  icon: <Building2 strokeWidth={1.6} />,
                } satisfies NavItem,
              ]
            : []),
          {
            id: "stores",
            label: m.nav.stores,
            href: "/stores",
            active: pathname.startsWith("/stores"),
            icon: <Store strokeWidth={1.6} />,
          } satisfies NavItem,
          {
            id: "members",
            label: m.nav.members,
            href: "/members",
            active: pathname.startsWith("/members"),
            icon: <Users strokeWidth={1.6} />,
          } satisfies NavItem,
          {
            id: "teams",
            label: m.nav.teams,
            href: "/teams",
            active: pathname.startsWith("/teams"),
            icon: <Users strokeWidth={1.6} />,
          } satisfies NavItem,
          ...(included("feature.module.billing_accounts") ? [{
            id: "billing",
            label: m.nav.billing,
            href: "/billing",
            active: pathname.startsWith("/billing"),
            icon: <CreditCard strokeWidth={1.6} />,
          } satisfies NavItem] : []),
        ]
      : []),
    {
      id: "settings",
      label: m.nav.settings,
      href: "/settings",
      active: pathname.startsWith("/settings"),
      icon: <Settings strokeWidth={1.6} />,
    },
  ];

  const navGroups = [
    {
      id: "operations",
      label: m.shell.navOperations,
      items: operationsItems,
    },
    {
      id: "inbox",
      label: m.shell.navInbox,
      items: inboxItems,
    },
    {
      id: "org",
      label: m.shell.navOrg,
      items: orgItems,
    },
  ];

  const flatNav = navGroups.flatMap((g) =>
    g.items.map((item) => ({ ...item, groupLabel: g.label })),
  );
  const active = flatNav.find((n) => n.active);
  const activeSection =
    navGroups.find((g) => g.items.some((n) => n.active))?.label ??
    m.shell.navOperations;
  const roleLabel =
    user?.clientOrgRole === "CLIENT_ADMIN"
      ? m.shell.clientAdminRole
      : user?.clientOrgRole === "STORE_USER"
        ? m.shell.storeUserRole
        : m.shell.clientStaffRole;

  return (
    <DashboardLayout
      brandMark={
        <img
          src="/favicon.png"
          alt=""
          className="size-full object-contain"
        />
      }
      brandMarkTransparent
      brand={<span>{m.appName}</span>}
      brandSubtitle={m.appSubtitle}
      navGroups={navGroups}
      pageCrumb={{
        section: activeSection,
        page: active?.label ?? m.nav.home,
      }}
      onNavigate={(href) => router.push(href)}
      headerExtras={<LanguageSwitcher className="h-8 w-[7.5rem] text-xs" />}
      notificationBell={included("feature.module.notification") ? <PortalNotificationBell /> : null}
      searchPlaceholder={m.shell.searchPlaceholder}
      profile={{
        name: user?.name ?? user?.email ?? "",
        role: roleLabel,
      }}
      commandLabels={{
        defaultGroup: m.common.navGroup,
        placeholder: m.common.goTo,
        empty: m.common.noCommands,
      }}
      commandItems={flatNav.map((n) => ({
        id: n.id,
        label: n.label,
        href: n.href,
        group: n.groupLabel,
      }))}
      a11yLabels={{
        mainNav: m.common.navGroup,
        openMenu: m.common.openMenu,
        closeMenu: m.common.closeMenu,
        close: m.common.close,
        toggleSidebar: m.shell.toggleSidebar,
        search: m.shell.searchAria,
        themeLight: m.shell.themeLight,
        themeDark: m.shell.themeDark,
        profileMenu: m.shell.profileMenu,
      }}
      userMenu={
        <>
          <div className="px-2 py-1.5">
            <p className="truncate text-sm font-medium">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {user?.email}
            </p>
          </div>
          <DropdownMenuSeparator />
          {isClientAdmin ? (
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => router.push("/members")}
            >
              {m.nav.members}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => router.push("/settings")}
          >
            {m.nav.settings}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:text-destructive"
            onSelect={() => {
              void logout().then(() => router.replace("/login"));
            }}
          >
            {m.logout}
          </DropdownMenuItem>
        </>
      }
    >
      {children}
    </DashboardLayout>
  );
}
